// Личные настройки вида таблицы протокола (012: data-model.md, user_views и protocol_views;
// research, Р11; решение 19). Масштаб — у пользователя, общий для его протоколов; свёрнутые
// ветви и «скрыто мной» — у пользователя по протоколу. Это вид экрана, а не данные протокола:
// ревизии, история и журнал действий не меняются (план, «Обоснованные отступления»).
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { COLUMN_KEY_PATTERN } from './item.ts';

/** Шаги масштаба таблицы, % (VIEW-14). */
export const zoomSteps = [70, 85, 100, 115, 130, 150] as const;
export type Zoom = (typeof zoomSteps)[number];
export const DEFAULT_ZOOM: Zoom = 100;
export const ZoomSchema = z.literal(zoomSteps);

/** Сколько номеров пунктов в одном списке вида (свёрнутые, скрытые мной). */
export const VIEW_LIST_MAX = 10_000;
const ViewListSchema = z.array(IdSchema).max(VIEW_LIST_MAX);

/** Персонально прочитанный ход работы: пункт → последняя показанная ревизия записи. */
export const ProgressSeenSchema = z
  .record(IdSchema, z.number().int().min(1).max(Number.MAX_SAFE_INTEGER))
  .refine((value) => Object.keys(value).length <= VIEW_LIST_MAX, 'Слишком много отметок чтения');

/** Ключ колонки в личном виде: встроенное поле или ключ дополнительного поля (013). */
export const ColumnKeySchema = z.string().regex(COLUMN_KEY_PATTERN);
/** Сколько ключей в списке вида колонок и пределы ширины при 100 % (013: data-model.md). */
export const VIEW_COLUMNS_MAX = 1000;
export const COLUMN_WIDTH_MIN = 40;
export const COLUMN_WIDTH_MAX = 2000;
const ColumnListSchema = z.array(ColumnKeySchema).max(VIEW_COLUMNS_MAX);
const ColumnWidthsSchema = z.record(
  ColumnKeySchema,
  z.number().int().min(COLUMN_WIDTH_MIN).max(COLUMN_WIDTH_MAX),
);

/**
 * Личный вид колонок (013: FR-020, решение 19): порядок, скрытые, ширины при 100 %. Новое поле,
 * которого нет в `order`, — в конце и видимо.
 */
export const ColumnsViewSchema = z.object({
  order: z.array(ColumnKeySchema),
  hidden: z.array(ColumnKeySchema),
  widths: z.record(ColumnKeySchema, z.number().int()),
});
export type ColumnsView = z.infer<typeof ColumnsViewSchema>;
export const defaultColumnsView: ColumnsView = { order: [], hidden: [], widths: {} };

/** Изменение вида колонок: изменившиеся ключи; «Наименование» скрыть нельзя. */
export const ColumnsViewPatchSchema = z
  .strictObject({
    order: ColumnListSchema.optional(),
    hidden: ColumnListSchema.optional(),
    widths: ColumnWidthsSchema.optional(),
  })
  .refine((patch) => !patch.hidden?.includes('title'), {
    message: 'Наименование скрыть нельзя',
    path: ['hidden'],
  });

/** Личный вид протокола в ответе minutes.state и minutes.view.save. */
export const ProtocolViewSchema = z.object({
  zoom: ZoomSchema,
  collapsed: z.array(IdSchema),
  hidden: z.array(IdSchema),
  columns: ColumnsViewSchema,
  /**
   * Фильтр «Моя зона» (020: research, Р10): null — пользователь ещё не выбирал (у «Редактора» с зоной
   * фильтр включён при первом открытии), true / false — его выбор.
   */
  myZone: z.boolean().nullable(),
  /** Отсутствует у прежних клиентов/видов; отметок чтения ещё нет. */
  progressSeen: ProgressSeenSchema.optional(),
});
export type ProtocolView = z.infer<typeof ProtocolViewSchema>;

/** Вид при первом открытии: 100 %, всё раскрыто, скрытых нет (ux CHK014), колонки по умолчанию. */
export const defaultProtocolView: ProtocolView = {
  zoom: DEFAULT_ZOOM,
  collapsed: [],
  hidden: [],
  columns: defaultColumnsView,
  myZone: null,
  progressSeen: {},
};

/** Изменение вида: изменившиеся ключи, списки — целиком. Пустой объект — REC-007. */
export const ProtocolViewPatchSchema = z
  .strictObject({
    zoom: ZoomSchema.optional(),
    collapsed: ViewListSchema.optional(),
    hidden: ViewListSchema.optional(),
    columns: ColumnsViewPatchSchema.optional(),
    myZone: z.boolean().optional(),
    /** Дельта с монотонным max; не заменяет весь сохранённый набор. */
    progressSeen: ProgressSeenSchema.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Нечего сохранять' });
export type ProtocolViewPatch = z.infer<typeof ProtocolViewPatchSchema>;
